
<template>
  <div class="stock-recipes">
    <div class="recipe-intro">
      <div>
        <h3>Stock Recipes</h3>
      </div>

      <button
        v-if="!formOpen"
        class="btn teal darken-2 waves-effect waves-light"
        type="button"
        :disabled="busy"
        @click="openNewRecipe"
      >
        <i class="material-icons left">add</i>
        New Recipe
      </button>
    </div>

    <div
      v-if="error"
      class="recipe-message error-message"
      role="alert"
    >
      {{ error }}
    </div>

    <div
      v-if="successMessage"
      class="recipe-message success-message"
      role="status"
    >
      {{ successMessage }}
    </div>

    <div v-if="busyLoad" class="progress">
      <div class="indeterminate"></div>
    </div>

    <form
      v-if="formOpen"
      class="recipe-form"
      @submit.prevent="saveRecipe"
    >
      <div class="recipe-form-heading">
        <div>
          <h4>
            {{ editingId ? "Edit Recipe" : "New Recipe" }}
          </h4>

          <span>Ingredients are stored in Sales Units.</span>
        </div>

        <button
          class="btn-flat"
          type="button"
          :disabled="busySave"
          @click="closeForm"
        >
          Close
        </button>
      </div>

      <div class="recipe-name-field">
        <label for="recipe-name">Recipe Name</label>

        <input
          id="recipe-name"
          v-model.trim="form.recipe_name"
          type="text"
          maxlength="160"
          autocomplete="off"
          placeholder="e.g. 1/2 Pint Shandy"
          required
        />
      </div>

      <div class="ingredient-heading-row">
        <strong>Ingredients and Recipes</strong>

        <div class="recipe-card-actions">
          <button
            class="btn-small teal darken-2 waves-effect waves-light"
            type="button"
            :disabled="busySave"
            @click="addIngredient"
          >
            <i class="material-icons left">add</i>
            Add Ingredient
          </button>

          <button
            class="btn-small teal darken-2 waves-effect waves-light"
            type="button"
            :disabled="busySave || !recipeOptions.length"
            @click="addRecipeComponent"
          >
            <i class="material-icons left">add</i>
            Add Recipe
          </button>
        </div>
      </div>

      <div
        v-if="form.ingredients.length"
        class="ingredient-table-wrap"
      >
        <table class="ingredient-table">
          <thead>
            <tr>
              <th>Stock Product</th>
              <th>Product Group</th>
              <th>Sales Units per Recipe</th>
              <th class="right-align">Action</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(ingredient, index) in form.ingredients"
              :key="ingredient.row_id"
            >
              <td>
                <select
                  v-model="ingredient.product_key"
                  class="browser-default recipe-select"
                  @change="ingredientChanged(ingredient)"
                >
                  <option value="">Select stock product</option>

                  <optgroup
                    v-for="group in groupedRecipeProductOptions"
                    :key="group.group_key"
                    :label="group.group_name"
                  >
                    <option
                      v-for="product in group.products"
                      :key="product.product_key"
                      :value="product.product_key"
                    >
                      {{ product.product_name }}
                    </option>
                  </optgroup>
                </select>
              </td>

              <td>
                {{ ingredient.product_group || "—" }}
              </td>

              <td>
                <input
                  v-model.number="ingredient.selling_units"
                  class="quantity-input"
                  type="number"
                  min="0.0001"
                  step="0.0001"
                  inputmode="decimal"
                />
              </td>

              <td class="right-align">
                <button
                  class="btn-small red darken-1 waves-effect waves-light"
                  type="button"
                  title="Remove ingredient"
                  :disabled="busySave"
                  @click="removeIngredient(index)"
                >
                  <i class="material-icons">delete</i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="form.recipe_components.length"
        class="ingredient-table-wrap"
      >
        <table class="ingredient-table">
          <thead>
            <tr>
              <th>Existing Recipe</th>
              <th>Quantity / Multiplier</th>
              <th class="right-align">Action</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(component, index) in form.recipe_components"
              :key="component.row_id"
            >
              <td>
                <select
                  v-model="component.recipe_id"
                  class="browser-default recipe-select"
                  @change="componentChanged(component)"
                >
                  <option value="">Select existing recipe</option>

                  <option
                    v-for="recipe in recipeOptions"
                    :key="recipe.id"
                    :value="recipe.id"
                  >
                    {{ recipe.recipe_name }}
                  </option>
                </select>
              </td>

              <td>
                <input
                  v-model.number="component.quantity"
                  class="quantity-input"
                  type="number"
                  min="0.0001"
                  step="0.0001"
                  inputmode="decimal"
                />

                <span class="grey-text text-darken-1">
                  1 = full recipe · 0.5 = half recipe
                </span>
              </td>

              <td class="right-align">
                <button
                  class="btn-small red darken-1 waves-effect waves-light"
                  type="button"
                  title="Remove included recipe"
                  :disabled="busySave"
                  @click="removeRecipeComponent(index)"
                >
                  <i class="material-icons">delete</i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="form.recipe_components.length"
        class="recipe-help"
      >
        <strong>Calculated stock usage</strong>

        <p>
          Included recipe quantities are multiplied and added to any individual
          ingredients above. Repeated products are combined automatically.
        </p>

        <div
          v-if="resolvedIngredients.length"
          class="recipe-summary"
        >
          <span
            v-for="ingredient in resolvedIngredients"
            :key="ingredient.product_key"
          >
            {{ ingredient.product_name }}:

            <strong>
              {{ formatNumber(ingredient.selling_units) }}
            </strong>

            Sales Units
          </span>
        </div>

        <p v-else>
          Select an existing recipe to preview the stock usage.
        </p>
      </div>

      <div class="recipe-help">
        Recipes use existing stock product keys and Sales Units. The Stock Count
        continues to receive the calculated ingredient quantities.
      </div>

      <div class="recipe-form-actions">
        <button
          class="btn teal darken-2 waves-effect waves-light"
          type="submit"
          :disabled="busySave || !formValid"
        >
          <i class="material-icons left">save</i>

          {{
            busySave
              ? "Saving..."
              : editingId
                ? "Save Changes"
                : "Save Recipe"
          }}
        </button>

        <button
          class="btn-flat"
          type="button"
          :disabled="busySave"
          @click="closeForm"
        >
          Cancel
        </button>
      </div>
    </form>

    <div
      v-if="!busyLoad && !recipes.length"
      class="empty-recipes"
    >
      No stock recipes have been created yet.
    </div>

    <div
      v-else-if="!busyLoad"
      class="recipe-list"
    >
      <article
        v-for="recipe in recipes"
        :key="recipe.id"
        class="recipe-card"
      >
        <div class="recipe-card-heading">
          <div>
            <h4>
              {{ recipe.recipe_name }}
            </h4>

            <span>
              {{ recipe.ingredients.length }}
              ingredient{{ recipe.ingredients.length === 1 ? "" : "s" }}
            </span>
          </div>

          <div class="recipe-card-actions">
            <button
              class="btn-small teal darken-2 waves-effect waves-light"
              type="button"
              title="Edit recipe"
              :disabled="busy"
              @click="editRecipe(recipe)"
            >
              <i class="material-icons">edit</i>
            </button>

            <button
              class="btn-small red darken-1 waves-effect waves-light"
              type="button"
              title="Delete recipe"
              :disabled="busy"
              @click="removeRecipe(recipe)"
            >
              <i class="material-icons">delete</i>
            </button>
          </div>
        </div>

        <div
          v-if="recipe.recipe_components.length"
          class="grey-text text-darken-1"
        >
          Includes:

          <span
            v-for="part in recipe.recipe_components"
            :key="part.recipe_id"
          >
            {{ part.recipe_name }} × {{ formatNumber(part.quantity) }}
          </span>
        </div>

        <div class="recipe-summary">
          <span
            v-for="ingredient in recipe.ingredients"
            :key="ingredient.product_key"
          >
            {{ ingredient.product_name }}:

            <strong>
              {{ formatNumber(ingredient.selling_units) }}
            </strong>

            Sales Units
          </span>
        </div>
      </article>
    </div>
  </div>
</template>

<script>
import {
  deleteStockRecipe,
  fetchStockRecipes,
  saveStockRecipe,
} from "@/scripts/sqlApiHelper/stockRecipesApi";

import {
  fetchStockOptions,
} from "@/scripts/sqlApiHelper/stockOptionsApi";

let rowSequence = 0;

const nextRowId = () => `recipe-row-${++rowSequence}`;

const textKey = (value) =>
  String(value ?? "")
    .trim()
    .toLocaleLowerCase("en-GB");

const round4 = (number) =>
  Math.round(
    (number + Number.EPSILON) * 10000
  ) / 10000;

const validQuantity = (value) => {
  const number = Number(value);

  return (
    Number.isFinite(number) &&
    number >= 0.0001 &&
    Math.abs(
      number * 10000 -
      Math.round(number * 10000)
    ) < 0.0000001
  );
};

function productKey(group, name) {
  return `${textKey(group)}::${textKey(name)}`;
}

function blankIngredient() {
  return {
    row_id: nextRowId(),
    product_key: "",
    product_name: "",
    product_group: "",
    selling_units: 1,
  };
}

function blankComponent() {
  return {
    row_id: nextRowId(),
    recipe_id: "",
    recipe_name: "",
    quantity: 1,
  };
}

function blankForm() {
  return {
    recipe_name: "",
    ingredients: [blankIngredient()],
    recipe_components: [],
  };
}

export default {
  name: "StockRecipes",

  props: {
    stockItems: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      recipes: [],
      form: blankForm(),
      editingId: "",
      formOpen: false,
      productGroupOrder: [],
      busyLoad: false,
      busySave: false,
      busyDelete: false,
      error: null,
      successMessage: null,
    };
  },

  computed: {
    busy() {
      return (
        this.busyLoad ||
        this.busySave ||
        this.busyDelete
      );
    },

    recipeProductOptions() {
      const products = new Map();

      for (const item of this.stockItems) {
        const name = String(item.item_name ?? "").trim();
        const group = String(item.product_group ?? "").trim();

        if (!name || !group) {
          continue;
        }

        const key = productKey(group, name);

        if (!products.has(key)) {
          products.set(key, {
            product_key: key,
            product_name: name,
            product_group: group,
          });
        }
      }

      return [...products.values()];
    },

    groupedRecipeProductOptions() {
      const groups = new Map();

      for (const product of this.recipeProductOptions) {
        const group_key = textKey(product.product_group);

        if (!groups.has(group_key)) {
          groups.set(group_key, {
            group_key,
            group_name: product.product_group,
            products: [],
          });
        }

        groups.get(group_key).products.push(product);
      }

      const order = new Map(
        this.productGroupOrder.map((name, index) => [
          textKey(name),
          index,
        ])
      );

      for (const group of groups.values()) {
        group.products.sort((a, b) =>
          a.product_name.localeCompare(
            b.product_name,
            "en-GB",
            {
              sensitivity: "base",
              numeric: true,
            }
          )
        );
      }

      return [...groups.values()].sort((a, b) => {
        const first =
          order.get(a.group_key) ??
          Number.MAX_SAFE_INTEGER;

        const second =
          order.get(b.group_key) ??
          Number.MAX_SAFE_INTEGER;

        return (
          first - second ||
          a.group_name.localeCompare(
            b.group_name,
            "en-GB"
          )
        );
      });
    },

    recipeOptions() {
      return this.recipes.filter(
        (recipe) =>
          recipe.id !== this.editingId &&
          !this.wouldCreateCycle(recipe.id)
      );
    },

    resolvedIngredients() {
      return this.calculateIngredients(this.recipes);
    },

    formValid() {
      if (!this.form.recipe_name.trim()) {
        return false;
      }

      const manualKeys = new Set();

      for (const item of this.form.ingredients) {
        if (!item.product_key) {
          continue;
        }

        if (
          !item.product_name ||
          !item.product_group ||
          !validQuantity(item.selling_units) ||
          manualKeys.has(item.product_key)
        ) {
          return false;
        }

        manualKeys.add(item.product_key);
      }

      for (const part of this.form.recipe_components) {
        if (
          !part.recipe_id ||
          !validQuantity(part.quantity) ||
          !this.recipeOptions.some(
            (recipe) => recipe.id === part.recipe_id
          )
        ) {
          return false;
        }
      }

      return this.resolvedIngredients.length > 0;
    },
  },

  async mounted() {
    await Promise.all([
      this.loadRecipes(),
      this.loadProductGroupOrder(),
    ]);
  },

  methods: {
    async loadProductGroupOrder() {
      try {
        const options = await fetchStockOptions();

        this.productGroupOrder = Array.isArray(
          options?.productGroups
        )
          ? [...options.productGroups]
          : [];
      } catch (error) {
        console.warn(
          "[STOCK RECIPES] Product group options unavailable:",
          error
        );

        this.productGroupOrder = [];
      }
    },

    async loadRecipes() {
      this.busyLoad = true;
      this.error = null;

      try {
        this.recipes = await fetchStockRecipes();
      } catch (error) {
        console.error(
          "[STOCK RECIPES] Load failed:",
          error
        );

        this.error =
          error?.message ||
          "Unable to load stock recipes.";
      } finally {
        this.busyLoad = false;
      }
    },

    wouldCreateCycle(
      recipeId,
      recipes = this.recipes
    ) {
      if (!this.editingId) {
        return false;
      }

      const byId = new Map(
        recipes.map((recipe) => [
          recipe.id,
          recipe,
        ])
      );

      const visited = new Set();
      const stack = [recipeId];

      while (stack.length) {
        const id = stack.pop();

        if (id === this.editingId) {
          return true;
        }

        if (visited.has(id)) {
          continue;
        }

        visited.add(id);

        const current = byId.get(id);

        for (
          const component of
          current?.recipe_components || []
        ) {
          stack.push(component.recipe_id);
        }
      }

      return false;
    },

    manualIngredients() {
      return this.form.ingredients
        .filter((item) => !!item.product_key)
        .map((item) => ({
          product_key: item.product_key,
          product_name: item.product_name,
          product_group: item.product_group,
          selling_units: Number(item.selling_units),
        }));
    },

    calculateIngredients(recipes) {
      const parts = this.manualIngredients().map(
        (item) => ({ ...item })
      );

      const byId = new Map(
        recipes.map((recipe) => [
          recipe.id,
          recipe,
        ])
      );

      for (const component of this.form.recipe_components) {
        const source = byId.get(component.recipe_id);
        const quantity = Number(component.quantity);

        if (
          !source ||
          !validQuantity(quantity) ||
          this.wouldCreateCycle(
            component.recipe_id,
            recipes
          )
        ) {
          continue;
        }

        for (const ingredient of source.ingredients || []) {
          parts.push({
            ...ingredient,
            selling_units:
              Number(ingredient.selling_units) *
              quantity,
          });
        }
      }

      const merged = new Map();

      for (const item of parts) {
        if (
          !item.product_key ||
          !item.product_name ||
          !item.product_group ||
          !(Number(item.selling_units) > 0)
        ) {
          continue;
        }

        const previous = merged.get(item.product_key);

        if (previous) {
          previous.selling_units += Number(
            item.selling_units
          );
        } else {
          merged.set(item.product_key, {
            product_key: item.product_key,
            product_name: item.product_name,
            product_group: item.product_group,
            selling_units: Number(item.selling_units),
          });
        }
      }

      return [...merged.values()]
        .map((item) => ({
          ...item,
          selling_units: round4(item.selling_units),
        }))
        .filter((item) => item.selling_units > 0);
    },

    openNewRecipe() {
      this.error = null;
      this.successMessage = null;
      this.editingId = "";
      this.form = blankForm();
      this.formOpen = true;
    },

    editRecipe(recipe) {
      this.error = null;
      this.successMessage = null;
      this.editingId = recipe.id;

      const manual = Array.isArray(
        recipe.base_ingredients
      )
        ? recipe.base_ingredients
        : recipe.ingredients;

      this.form = {
        recipe_name: String(
          recipe.recipe_name ?? ""
        ),

        ingredients: (manual || []).map(
          (item) => ({
            ...blankIngredient(),
            product_key: String(
              item.product_key ?? ""
            ),
            product_name: String(
              item.product_name ?? ""
            ),
            product_group: String(
              item.product_group ?? ""
            ),
            selling_units:
              Number(item.selling_units) || 0,
          })
        ),

        recipe_components: (
          recipe.recipe_components || []
        ).map((part) => ({
          ...blankComponent(),
          recipe_id: String(
            part.recipe_id ?? ""
          ),
          recipe_name: String(
            part.recipe_name ?? ""
          ),
          quantity: Number(part.quantity) || 0,
        })),
      };

      if (
        !this.form.ingredients.length &&
        !this.form.recipe_components.length
      ) {
        this.form.ingredients.push(
          blankIngredient()
        );
      }

      this.formOpen = true;

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },

    closeForm() {
      if (this.busySave) {
        return;
      }

      this.formOpen = false;
      this.editingId = "";
      this.form = blankForm();
    },

    addIngredient() {
      this.form.ingredients.push(
        blankIngredient()
      );
    },

    removeIngredient(index) {
      this.form.ingredients.splice(index, 1);
    },

    ingredientChanged(ingredient) {
      const selected = this.recipeProductOptions.find(
        (product) =>
          product.product_key === ingredient.product_key
      );

      ingredient.product_name =
        selected?.product_name || "";

      ingredient.product_group =
        selected?.product_group || "";
    },

    addRecipeComponent() {
      if (
        this.form.ingredients.length === 1 &&
        !this.form.ingredients[0].product_key
      ) {
        this.form.ingredients = [];
      }

      this.form.recipe_components.push(
        blankComponent()
      );
    },

    removeRecipeComponent(index) {
      this.form.recipe_components.splice(index, 1);
    },

    componentChanged(component) {
      const source = this.recipes.find(
        (recipe) =>
          recipe.id === component.recipe_id
      );

      component.recipe_name =
        source?.recipe_name || "";
    },

    async saveRecipe() {
      if (!this.formValid || this.busySave) {
        return;
      }

      this.busySave = true;
      this.error = null;
      this.successMessage = null;

      try {
        // Refresh sources before saving so the recipe uses current quantities.
        const current = await fetchStockRecipes();

        const duplicate = current.find(
          (recipe) =>
            recipe.id !== this.editingId &&
            textKey(recipe.recipe_name) ===
              textKey(this.form.recipe_name)
        );

        if (duplicate) {
          throw new Error(
            "A recipe with this name already exists."
          );
        }

        for (const part of this.form.recipe_components) {
          const source = current.find(
            (recipe) =>
              recipe.id === part.recipe_id
          );

          if (!source) {
            throw new Error(
              "An included recipe no longer exists."
            );
          }

          if (
            this.wouldCreateCycle(
              part.recipe_id,
              current
            )
          ) {
            throw new Error(
              "A recipe cannot include itself, directly or indirectly."
            );
          }
        }

        const flattened = this.calculateIngredients(
          current
        );

        if (!flattened.length) {
          throw new Error(
            "No valid stock ingredients found."
          );
        }

        await saveStockRecipe({
          recipeId: this.editingId,
          recipeName: this.form.recipe_name,
          ingredients: flattened,
          baseIngredients: this.manualIngredients(),

          recipeComponents: this.form.recipe_components.map(
            (part) => ({
              recipe_id: part.recipe_id,

              recipe_name:
                current.find(
                  (recipe) =>
                    recipe.id === part.recipe_id
                )?.recipe_name ||
                part.recipe_name,

              quantity: Number(part.quantity),
            })
          ),
        });

        const message = this.editingId
          ? "Recipe updated successfully."
          : "Recipe created successfully.";

        this.formOpen = false;
        this.editingId = "";
        this.form = blankForm();

        await this.loadRecipes();

        this.successMessage = message;
      } catch (error) {
        console.error(
          "[STOCK RECIPES] Save failed:",
          error
        );

        this.error =
          error?.message ||
          "Unable to save the recipe.";
      } finally {
        this.busySave = false;
      }
    },

    async removeRecipe(recipe) {
      if (this.busyDelete) {
        return;
      }

      const usedBy = this.recipes.find(
        (other) =>
          other.id !== recipe.id &&
          (
            other.recipe_components || []
          ).some(
            (part) =>
              part.recipe_id === recipe.id
          )
      );

      if (usedBy) {
        this.error =
          `Cannot delete ${recipe.recipe_name}: ` +
          `it is included in ${usedBy.recipe_name}.`;

        return;
      }

      if (
        !window.confirm(
          `Delete recipe "${recipe.recipe_name}"?\n\nThis cannot be undone.`
        )
      ) {
        return;
      }

      this.busyDelete = true;
      this.error = null;
      this.successMessage = null;

      try {
        await deleteStockRecipe(recipe.id);
        await this.loadRecipes();

        this.successMessage =
          "Recipe deleted successfully.";
      } catch (error) {
        console.error(
          "[STOCK RECIPES] Delete failed:",
          error
        );

        this.error =
          error?.message ||
          "Unable to delete the recipe.";
      } finally {
        this.busyDelete = false;
      }
    },

    formatNumber(value) {
      const number = Number(value);

      if (!Number.isFinite(number)) {
        return "0";
      }

      return new Intl.NumberFormat("en-GB", {
        maximumFractionDigits: 4,
      }).format(number);
    },
  },
};
</script>

<style scoped>
.stock-recipes {
  width: 100%;
}

.recipe-intro,
.recipe-form-heading,
.ingredient-heading-row,
.recipe-card-heading,
.recipe-form-actions,
.recipe-card-actions {
  display: flex;
  align-items: center;
}

.recipe-intro,
.recipe-form-heading,
.ingredient-heading-row,
.recipe-card-heading {
  justify-content: space-between;
  gap: 20px;
}

.recipe-intro h3,
.recipe-form h4,
.recipe-card h4 {
  margin: 0;
  color: #37474f;
}

.recipe-intro p {
  max-width: 780px;
  margin: 7px 0 0;
  color: #607d8b;
}

.recipe-form {
  margin-top: 22px;
  padding: 22px;
  border: 1px solid #d9e1e5;
  border-radius: 10px;
  background: #f8fafb;
}

.recipe-form-heading span,
.recipe-card-heading span {
  color: #78909c;
  font-size: 0.9rem;
}

.recipe-name-field {
  max-width: 520px;
  margin-top: 20px;
}

.recipe-name-field label {
  display: block;
  margin-bottom: 5px;
  color: #455a64;
  font-weight: 700;
}

.ingredient-heading-row {
  margin: 20px 0 10px;
}

.ingredient-table-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.ingredient-table {
  min-width: 760px;
}

.recipe-select,
.quantity-input {
  width: 100%;
  min-width: 160px;
  height: 40px;
  margin: 0;
}

.quantity-input {
  max-width: 180px;
}

.recipe-help {
  margin-top: 12px;
  color: #78909c;
  font-size: 0.85rem;
}

.recipe-form-actions {
  gap: 10px;
  margin-top: 22px;
}

.recipe-list {
  display: grid;
  grid-template-columns: repeat(
    auto-fit,
    minmax(330px, 1fr)
  );
  gap: 14px;
  margin-top: 22px;
}

.recipe-card {
  padding: 18px;
  border: 1px solid #d9e1e5;
  border-radius: 10px;
  background: #ffffff;
}

.recipe-card-actions {
  gap: 7px;
  flex-wrap: wrap;
}

.recipe-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 14px;
}

.recipe-summary span {
  padding: 5px 9px;
  border-radius: 999px;
  background: #eceff1;
  color: #455a64;
  font-size: 0.87rem;
}

.empty-recipes {
  margin-top: 22px;
  padding: 26px;
  border: 1px dashed #b0bec5;
  border-radius: 10px;
  color: #78909c;
  text-align: center;
}

.recipe-message {
  margin-top: 16px;
  padding: 11px 14px;
  border-radius: 6px;
}

.error-message {
  border: 1px solid #ef9a9a;
  background: #ffebee;
  color: #b71c1c;
}

.success-message {
  border: 1px solid #a5d6a7;
  background: #e8f5e9;
  color: #1b5e20;
}

@media only screen and (max-width: 700px) {
  .recipe-intro,
  .recipe-form-heading {
    align-items: stretch;
    flex-direction: column;
  }

  .recipe-list {
    grid-template-columns: 1fr;
  }
}
</style>
